import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { fetchApi } from "@/lib/apiBase";
import { DEFAULT_PUBLIC_HOURS_ROWS, getPublicHoursRows } from "@/lib/publicHours";
const SALON_ADDRESS = "TheLuxeLoft - 507 S 52nd St, Philadelphia, PA 19143";
const ENCODED_SALON_ADDRESS = encodeURIComponent(SALON_ADDRESS);
const GOOGLE_MAPS_URL = `https://www.google.com/maps/search/?api=1&query=${ENCODED_SALON_ADDRESS}`;
const APPLE_MAPS_URL = `https://maps.apple.com/?q=${ENCODED_SALON_ADDRESS}`;

const contactInfo = [
  {
    icon: Mail,
    label: "Email",
    value: "bookings@dadadeluxe.com",
    href: "mailto:bookings@dadadeluxe.com",
  },
  {
    icon: Phone,
    label: "Phone",
    value: "(215) 760-9772",
    href: "tel:+12157609772",
  },
  {
    icon: MapPin,
    label: "Location",
    value: SALON_ADDRESS,
    href: GOOGLE_MAPS_URL,
  },
];

export default function ContactPage() {
  const [hours, setHours] = useState(DEFAULT_PUBLIC_HOURS_ROWS);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const revealRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.1 }
    );

    revealRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let active = true;

    getPublicHoursRows().then((rows) => {
      if (active) {
        setHours(rows);
      }
    });

    return () => {
      active = false;
    };
  }, []);

  const addRevealRef = (el) => {
    if (el && !revealRefs.current.includes(el)) {
      revealRefs.current.push(el);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetchApi("/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!res.ok) throw new Error("Failed to submit");

      toast.success("Message sent! We'll get back to you soon.");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-[#F0F0F0] min-h-screen">
      {/* Header */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <h1 className="script-heading text-3xl md:text-4xl mb-4">
            Contact Us
          </h1>
          <p className="text-[#666] max-w-lg mx-auto">
            Have a question or want to learn more? I'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section 
        data-testid="contact-section" 
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16"
      >
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Contact Form */}
          <div ref={addRevealRef} className="reveal glass-card p-6 md:p-8">
            <h2 className="font-medium text-lg mb-6">Send a Message</h2>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="name" className="text-sm text-[#666]">
                  Full Name *
                </Label>
                <Input
                  id="name"
                  data-testid="contact-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  autoComplete="name"
                  className="mt-1 luxury-input"
                  placeholder="Jane Doe"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-sm text-[#666]">
                  Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="contact-email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  autoComplete="email"
                  inputMode="email"
                  className="mt-1 luxury-input"
                  placeholder="jane@example.com"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-sm text-[#666]">
                  Phone (optional)
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  data-testid="contact-phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  autoComplete="tel"
                  inputMode="tel"
                  className="mt-1 luxury-input"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <Label htmlFor="message" className="text-sm text-[#666]">
                  Message *
                </Label>
                <Textarea
                  id="message"
                  data-testid="contact-message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="mt-1 luxury-input min-h-[120px]"
                  placeholder="How can we help you?"
                />
              </div>

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={isSubmitting}
                className="btn-black w-full flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner" /> Sending...
                  </>
                ) : (
                  <>
                    Send Message <Send size={14} />
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div ref={addRevealRef} className="reveal space-y-6" style={{ animationDelay: "200ms" }}>
            {/* Info Cards */}
            <div className="glass-card p-6">
              <h2 className="font-medium text-lg mb-4">Contact Information</h2>
              <div className="space-y-4">
                {contactInfo.map((item, index) => (
                  <div
                    key={index}
                    data-testid={`contact-info-${index}`}
                    className="flex items-start gap-3"
                  >
                    <div className="w-10 h-10 bg-[#F0F0F0] rounded-full flex items-center justify-center flex-shrink-0">
                      <item.icon className="text-[#111]" size={18} />
                    </div>
                    <div>
                      <p className="text-xs text-[#888] mb-0.5">{item.label}</p>
                      {item.href ? (
                        <>
                          <a
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#111] hover:text-[#666] transition-colors underline underline-offset-2"
                          >
                            {item.value}
                          </a>
                          {item.label === "Location" && (
                            <div className="mt-1 flex flex-wrap gap-3 text-xs">
                              <a
                                href={APPLE_MAPS_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#666] hover:text-[#111] transition-colors underline underline-offset-2"
                              >
                                Open in Maps
                              </a>
                              <a
                                href={GOOGLE_MAPS_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#666] hover:text-[#111] transition-colors underline underline-offset-2"
                              >
                                Open in Google Maps
                              </a>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-[#111]">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Hours */}
            <div className="hours-table">
              <div className="p-4 border-b border-white/10 flex items-center gap-2">
                <Clock size={16} />
                <h3 className="font-medium">Hours of Operation</h3>
              </div>
              {hours.map((item, index) => (
                <div key={index} data-testid={`hours-${index}`} className="hours-row">
                  <span>{item.day}</span>
                  <span className="text-white/80">{item.time}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
